import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "src/components/common/Spinner";
import SearchBar from "src/components/common/SearchBar";
import { CreateEmployeeModal } from "src/components/CreateEmployeeModal/CreateEmployeeModal";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { useAuth } from "src/contexts/auth-provider";
import { FEATURE_NAMES } from "src/utils/isAllowedFeature";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import useOutsideClick from "src/hooks/useOutsideClick";
import { GET_EMPLOYEE } from "src/constants/queries";
import {
  delete_employee_modal,
  search_employees,
  set_employees,
} from "src/store/features/employee-slice";
import { ReportPageLayout } from "src/features/Reports/components/ReportPageLayout";
import { ReportTabs } from "src/features/Reports/components/ReportTabs";
import { TimecardsTab } from "src/features/Timecards/components/TimecardsTab";

const BORDER_LIGHT = "rgba(0,0,0,0.06)";

type EmployeeTab = "team" | "timecards";

function canAccessTimecards(userRole: string | null): boolean {
  if (!userRole) return false;
  return (
    userRole === "ROLE_MANAGER" ||
    userRole === "ROLE_ADMIN" ||
    userRole === "ROLE_STORE_OWNER" ||
    userRole === "ROLE_PLATFORM_ADMIN"
  );
}

function formatRole(employee: { storeStaffRole?: string; roles?: string[] }): string {
  if (employee?.storeStaffRole === "OWNER") return "Owner";
  const role = employee?.roles?.[0];
  if (!role) return "-";
  // Backend enum: ROLE_EMPLOYEE, ROLE_MANAGER, etc. Strip "ROLE_" and title-case.
  const name = role.replace(/^ROLE_/, "").toLowerCase();
  return name.charAt(0).toUpperCase() + name.slice(1);
}

interface EmployeeRow {
  correlationId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  employerName?: string;
  roles?: string[];
  storeStaffRole?: string;
  isOwner?: boolean;
}

function EmployeeRowActionsMenu({
  onClose,
  employee,
  onEdit,
  onDelete,
}: {
  onClose: () => void;
  employee: EmployeeRow;
  onEdit: (employee: EmployeeRow) => void;
  onDelete: (employee: EmployeeRow) => void;
}) {
  return (
    <div
      className="absolute right-0 top-full mt-1 z-10 min-w-[128px] py-1 bg-white rounded-xl border border-[#F1F1F2] shadow-[0_4px_12px_rgba(0,0,0,0.08)] overflow-hidden"
      role="menu"
    >
      <button
        type="button"
        className="w-full text-left px-4 py-2.5 text-sm text-[#828282] hover:bg-[#f1f1f2] hover:text-[#252525]"
        onClick={() => {
          onEdit(employee);
          onClose();
        }}
      >
        Edit
      </button>
      {employee.storeStaffRole !== "OWNER" && (
        <button
          type="button"
          className="w-full text-left px-4 py-2.5 text-sm text-[#828282] hover:bg-red-50 hover:text-red-600"
          onClick={() => {
            onDelete(employee);
            onClose();
          }}
        >
          Delete
        </button>
      )}
    </div>
  );
}

function EmployeeRowActions({
  employee,
  onEdit,
  onDelete,
}: {
  employee: EmployeeRow;
  onEdit: (employee: EmployeeRow) => void;
  onDelete: (employee: EmployeeRow) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick({ ref, handler: () => setOpen(false) });

  return (
    <div
      ref={ref}
      className="rowActions relative flex justify-end opacity-0 transition-opacity duration-[120ms] ease-out group-hover:opacity-100 group-focus-within:opacity-100"
    >
      <button
        type="button"
        className="w-10 h-10 min-w-[40px] min-h-[40px] flex items-center justify-center rounded-[10px] text-gray-400 hover:text-gray-600 active:bg-gray-100"
        aria-label="Row actions"
        onClick={() => setOpen((o) => !o)}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
          <circle cx="10" cy="4" r="1.5" />
          <circle cx="10" cy="10" r="1.5" />
          <circle cx="10" cy="16" r="1.5" />
        </svg>
      </button>
      {open && (
        <EmployeeRowActionsMenu
          employee={employee}
          onClose={() => setOpen(false)}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      )}
    </div>
  );
}

const Employee = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") as EmployeeTab | null;
  const activeTab: EmployeeTab =
    tabParam === "timecards" ? "timecards" : "team";

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { hasRestaurant, user, permissions, userRole } = useAuth();
  const canInviteEmployee = permissions[FEATURE_NAMES.TeamManagement];
  const showTimecardsTab = canAccessTimecards(userRole ?? null);
  const { axBe } = useAxiosInterceptor();
  const dispatch = useDispatch();

  const { filteredEmployees: data } = useSelector(
    (state: any) => state.employee
  );

  const displayList: EmployeeRow[] = data || [];

  const handleDeleteEmployee = (employee: EmployeeRow) => {
    dispatch(delete_employee_modal(employee.correlationId));
  };

  const getEmployees = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    setIsLoading(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_EMPLOYEE,
        variables: {
          storeCorrelationId: hasRestaurant.correlationId,
        },
      });
      dispatch(
        set_employees({
          employees: data?.data?.storeEmployeesByStoreCorrelationId || [],
        })
      );
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  }, [hasRestaurant?.correlationId, axBe, dispatch]);

  useEffect(() => {
    if (activeTab === "team" && hasRestaurant?.correlationId) getEmployees();
  }, [activeTab, hasRestaurant?.correlationId, getEmployees]);

  const handleEditEmployee = (editData: EmployeeRow) => {
    setEditValues({
      ...editData,
      isOwner: editData.storeStaffRole === "OWNER",
    });
    setShowCreateModal(true);
  };

  const applySearch = (value: string) => {
    dispatch(search_employees(value));
  };

  const setTab = (t: EmployeeTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", t);
      return next;
    });
  };

  const tabs = [
    { id: "team", label: "Team" },
    ...(showTimecardsTab ? [{ id: "timecards", label: "Timecards" }] : []),
  ];

  return (
    <ReportPageLayout
      fillHeight
      compactTop
      toolbarMarginTop={0}
      toolbar={
        <div className="flex items-center justify-between gap-4 w-full min-w-0">
          <ReportTabs
            tabs={tabs}
            activeTabId={activeTab}
            onSelect={(id) => setTab(id as EmployeeTab)}
            inline
          />
        </div>
      }
    >
      {activeTab === "timecards" && showTimecardsTab && (
        <div className="flex-1 min-h-0 flex flex-col">
          <TimecardsTab />
        </div>
      )}
      {activeTab === "team" && (
        <div className="w-full max-w-full flex-1 min-h-0 flex flex-col">
          <div
            className="bg-white rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.04)] overflow-hidden flex-1 flex flex-col min-h-0"
            style={{ border: `1px solid ${BORDER_LIGHT}` }}
          >
            <section
              className="shrink-0 py-2 px-3 border-b flex flex-row items-center justify-between gap-3 min-h-[42px]"
              style={{ borderColor: BORDER_LIGHT }}
              aria-label="Employees toolbar"
            >
              <div className="flex-1 min-w-0 max-w-[260px]">
                <SearchBar
                  placeholder="Search by name"
                  onDebouncedChange={applySearch}
                  debounceMs={300}
                />
              </div>
              {canInviteEmployee && (
                <button
                  type="button"
                  className="h-8 px-4 rounded-lg text-sm font-medium text-white bg-[#FD7657] hover:bg-[#e45e41] shrink-0 transition-colors"
                  onClick={() => {
                    setEditValues({});
                    setShowCreateModal(true);
                  }}
                >
                  Add Employee
                </button>
              )}
            </section>

            {isLoading && displayList.length === 0 ? (
              <div className="flex justify-center py-12 flex-1 min-h-[120px]">
                <Spinner />
              </div>
            ) : displayList.length === 0 ? (
              <p className="text-gray-500 text-sm py-12 text-center flex-1">
                No employees found
              </p>
            ) : (
              <section
                className="flex-1 min-h-0 flex flex-col min-w-0 relative"
                aria-label="Employees table"
                aria-busy={isLoading}
              >
                {isLoading && (
                  <div
                    className="absolute inset-0 z-20 flex items-center justify-center bg-white/70"
                    aria-hidden="true"
                  >
                    <Spinner />
                  </div>
                )}
                <div className="w-full flex-1 min-h-0 flex flex-col min-w-0 overflow-hidden">
                  <div className="flex-1 min-h-0 overflow-auto">
                    <table className="w-full text-left border-collapse table-fixed" style={{ minWidth: 720 }}>
                      <colgroup>
                        <col style={{ width: "14%" }} />
                        <col style={{ width: "14%" }} />
                        <col style={{ width: "20%" }} />
                        <col style={{ width: "14%" }} />
                        <col style={{ width: "14%" }} />
                        <col style={{ width: "14%" }} />
                        <col style={{ width: "52px" }} />
                      </colgroup>
                      <thead>
                        <tr className="text-[12px] font-semibold uppercase tracking-wide text-[#374151] border-b border-[#e2e8f0]">
                          <th className="sticky top-0 z-10 bg-[#f1f5f9] py-3 pl-4 pr-3 align-middle whitespace-nowrap">First name</th>
                          <th className="sticky top-0 z-10 bg-[#f1f5f9] py-3 px-3 align-middle whitespace-nowrap">Last name</th>
                          <th className="sticky top-0 z-10 bg-[#f1f5f9] py-3 px-3 align-middle whitespace-nowrap">Email</th>
                          <th className="sticky top-0 z-10 bg-[#f1f5f9] py-3 px-3 align-middle whitespace-nowrap">Phone</th>
                          <th className="sticky top-0 z-10 bg-[#f1f5f9] py-3 px-3 align-middle whitespace-nowrap">Employer name</th>
                          <th className="sticky top-0 z-10 bg-[#f1f5f9] py-3 px-3 align-middle whitespace-nowrap">Role</th>
                          <th className="sticky top-0 z-10 bg-[#f1f5f9] py-3 pl-3 pr-4 align-middle"><span className="sr-only">Actions</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayList.map((employee, index) => (
                            <tr
                              key={employee.correlationId ?? index}
                              className="group h-12 transition-[background-color] duration-[120ms] ease-out hover:bg-[#fff7f2]"
                              style={{ borderBottom: `1px solid ${BORDER_LIGHT}` }}
                            >
                              <td className="py-0 pl-4 pr-3 align-middle overflow-hidden">
                                <div className="py-1.5 min-w-0">
                                  <div className="text-sm font-medium text-gray-900 truncate" title={employee.firstName ?? undefined}>
                                    {employee.firstName ?? "-"}
                                  </div>
                                </div>
                              </td>
                              <td className="py-0 px-3 align-middle overflow-hidden">
                                <div className="text-sm text-gray-600 truncate" title={employee.lastName ?? undefined}>
                                  {employee.lastName ?? "-"}
                                </div>
                              </td>
                              <td className="py-0 px-3 align-middle overflow-hidden">
                                <div className="text-sm text-gray-600 truncate" title={employee.email ?? undefined}>
                                  {employee.email ?? "-"}
                                </div>
                              </td>
                              <td className="py-0 px-3 align-middle overflow-hidden">
                                <div className="text-sm text-gray-600 truncate" title={employee.phoneNumber ?? undefined}>
                                  {employee.phoneNumber ?? "-"}
                                </div>
                              </td>
                              <td className="py-0 px-3 align-middle overflow-hidden">
                                <div className="text-sm text-gray-600 truncate" title={employee.employerName ?? undefined}>
                                  {employee.employerName ?? "-"}
                                </div>
                              </td>
                              <td className="py-0 px-3 align-middle overflow-hidden">
                                <span className="text-sm text-gray-700">{formatRole(employee)}</span>
                              </td>
                              <td className="py-0 pl-3 pr-4 align-middle" onClick={(e) => e.stopPropagation()}>
                                <EmployeeRowActions
                                  employee={employee}
                                  onEdit={handleEditEmployee}
                                  onDelete={handleDeleteEmployee}
                                />
                              </td>
                            </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            )}
          </div>
        </div>
      )}

      {showCreateModal && (
        <CreateEmployeeModal
          onClose={() => setShowCreateModal(false)}
          editValues={editValues}
          getEmployees={getEmployees}
          setEditValues={setEditValues}
        />
      )}
    </ReportPageLayout>
  );
};

export default Employee;
